import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { ArrowRight, Check, X, Sparkles, Clock, ShieldCheck, Leaf, MousePointerClick, Users, DollarSign, Phone, Truck, MapPin, Clock3 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOSchema from "@/components/SEOSchema";
import { PRICING_TIERS, SERVICE_AREAS, WE_REMOVE, WE_DONT_REMOVE, FAQ_ITEMS, BENEFITS, STAGING_GALLERY } from "@/lib/constants";

const SINGLE_HOVER_IMG = "https://customer-assets.emergentagent.com/job_trash-removal-cle/artifacts/ac6m1px9_comegetmytrashcouch.png";
const DUO_HOVER_IMG = "https://customer-assets.emergentagent.com/job_trash-removal-cle/artifacts/26627jqf_Duocomegetmytrash.png";
const TRIO_HOVER_IMG = "https://customer-assets.emergentagent.com/job_trash-removal-cle/artifacts/bn6t62pj_triocomegetmytrash.png";
const HAUL_HOVER_IMG = "https://customer-assets.emergentagent.com/job_trash-removal-cle/artifacts/q5pg5acj_haulcomegetmytrash.png";

const TIER_HOVER = {
  "1_item": {
    img: SINGLE_HOVER_IMG,
    title: "Single pickup",
    sub: "One item, staged at the curb or driveway. That's all we need.",
  },
  "2_items": {
    img: DUO_HOVER_IMG,
    title: "Duo pickup",
    sub: "Two items, one trip. Washer + dryer, sofa + loveseat — you name it.",
  },
  "3_items": {
    img: TRIO_HOVER_IMG,
    title: "Trio pickup",
    sub: "Three items, one trip. Sectionals, appliances, bedroom sets — handled.",
  },
  "4_plus": {
    img: HAUL_HOVER_IMG,
    title: "Haul pickup",
    sub: "4+ items. Mattress, washer, dryer, fridge — lined up and gone.",
  },
};

const BENEFIT_ICONS = [DollarSign, Clock, MousePointerClick, Leaf, Sparkles, Users];

export default function LandingPage() {
  const navigate = useNavigate();

  const goBook = (tierId) => {
    const q = tierId ? `?tier=${tierId}` : "";
    navigate(`/book${q}`);
  };

  return (
    <div data-testid="landing-page" className="bg-white">
      <SEOSchema />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1a1a1a]">
        {/* Background image */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HAUL_HOVER_IMG})` }}
        />
        {/* Orange gradient overlay for brand feel + legibility */}
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(100deg, rgba(255,107,53,0.92) 0%, rgba(255,107,53,0.78) 45%, rgba(255,140,66,0.55) 70%, rgba(26,26,26,0.35) 100%)",
          }}
        />
        {/* Subtle diagonal stripe texture */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-20 mix-blend-overlay"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg, rgba(255,255,255,0.06) 0 2px, transparent 2px 16px)",
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-28 text-white relative">
          <div className="max-w-3xl animate-fade-up">
            <Badge className="bg-white/20 hover:bg-white/25 text-white border border-white/20 mb-4 sm:mb-5 backdrop-blur">
              Same-day available • Cleveland, OH
            </Badge>
            <h1 className="font-display text-[2.75rem] leading-[0.95] sm:text-6xl lg:text-7xl font-black tracking-tight drop-shadow-[0_2px_12px_rgba(26,26,26,0.35)]">
              Come Pick Up<br />My Trash.
            </h1>
            <p className="mt-4 sm:mt-5 text-base sm:text-xl text-white/95 max-w-2xl drop-shadow-[0_1px_6px_rgba(26,26,26,0.3)]">
              Simple. Transparent. Reliable junk removal in Cleveland. Starting at $59. No hidden fees. No quotes. Just book and we haul.
            </p>
            <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-3 max-w-md sm:max-w-none">
              <Button
                data-testid="hero-book-btn"
                onClick={() => goBook()}
                className="bg-white text-[#ff6b35] hover:bg-[#fffbf0] font-bold rounded-lg shadow-lg h-12 px-6 text-base"
              >
                Book Your Pickup Now <ArrowRight size={18} className="ml-1" />
              </Button>
              <a
                href="#pricing"
                data-testid="hero-see-pricing"
                className="inline-flex items-center justify-center h-12 px-6 rounded-lg border-2 border-white/40 hover:bg-white/10 font-bold transition-colors"
              >
                See pricing
              </a>
            </div>
            <div className="mt-6 text-white/80 text-sm">
              Starting at <span className="font-bold text-white">$59</span> • Same-day available • No hidden fees
            </div>
          </div>
        </div>
        <div className="absolute -bottom-10 -right-10 w-64 h-64 rounded-full bg-white/10 blur-3xl pointer-events-none" />
        <div className="absolute top-10 right-20 w-40 h-40 rounded-full bg-white/5 blur-2xl pointer-events-none" />
      </section>

      {/* NAP (Name · Address · Phone) TRUST STRIP — visible for local SEO */}
      <section data-testid="nap-strip" className="bg-[#fffbf0] border-y border-[#1a1a1a]/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#ff6b35]/10 text-[#ff6b35] flex-shrink-0">
              <MapPin size={16} />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold text-[#ff6b35] uppercase tracking-widest">Serving</div>
              <div className="font-bold text-[#1a1a1a] truncate">Cleveland &amp; Cuyahoga County, OH</div>
            </div>
          </div>
          <a href="tel:+12163062330" className="flex items-center gap-2 group">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#ff6b35]/10 text-[#ff6b35] flex-shrink-0 group-hover:bg-[#ff6b35] group-hover:text-white transition-colors">
              <Phone size={16} />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold text-[#ff6b35] uppercase tracking-widest">Call or text</div>
              <div className="font-bold text-[#1a1a1a] truncate">(216) 306-2330</div>
            </div>
          </a>
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#ff6b35]/10 text-[#ff6b35] flex-shrink-0">
              <Clock3 size={16} />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold text-[#ff6b35] uppercase tracking-widest">Hours</div>
              <div className="font-bold text-[#1a1a1a] truncate">Mon–Sun · 7 AM–7 PM</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-[#ff6b35]/10 text-[#ff6b35] flex-shrink-0">
              <ShieldCheck size={16} />
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold text-[#ff6b35] uppercase tracking-widest">Trust</div>
              <div className="font-bold text-[#1a1a1a] truncate">Licensed · Insured · Local</div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-[#ff6b35] font-bold text-sm uppercase tracking-widest mb-2">Transparent pricing</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold">Price quoted is price you pay.</h2>
            <p className="text-[#333] mt-4 text-lg">
              Flat-rate tiers based on how much you've got. No surprises. No hidden fees.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRICING_TIERS.map((t, i) => {
              const hover = TIER_HOVER[t.id];
              const card = (
                <div
                  key={t.id}
                  data-testid={`pricing-card-${t.id}`}
                  className="bg-white rounded-xl border-[3px] border-[#ff6b35] flex flex-col hover:-translate-y-1 transition-transform shadow-sm hover:shadow-lg h-full overflow-hidden"
                >
                  {hover && (
                    <div className="relative w-full aspect-[4/3] bg-[#fffbf0] overflow-hidden">
                      <img
                        src={hover.img}
                        alt={`${t.name} example — furniture staged in driveway`}
                        loading="lazy"
                        data-testid={`thumb-${t.id}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 left-2 bg-[#ff6b35] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded">
                        {t.name}
                      </div>
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-baseline gap-1">
                      <span className="font-display text-5xl font-black text-[#1a1a1a]">${t.price}</span>
                      {t.plus && <span className="text-[#ff6b35] font-bold text-xl">+</span>}
                    </div>
                    <div className="text-[#333] text-sm mt-1">{t.itemCount}{t.plus ? "+" : ""} item{t.itemCount > 1 ? "s" : ""}</div>
                    <p className="mt-3 text-[#1a1a1a] font-semibold">{t.tagline}</p>
                    <ul className="mt-3 space-y-2 text-sm text-[#333] flex-1">
                      {t.examples.map((ex) => (
                        <li key={ex.label} className="flex gap-2 items-start">
                          <Check size={14} className="text-[#ff6b35] mt-1 flex-shrink-0" />
                          <span>{ex.label}</span>
                        </li>
                      ))}
                    </ul>
                    {t.pricingNote && (
                      <div className="mt-3 text-xs font-bold text-[#ff6b35] bg-[#fffbf0] border-l-[3px] border-[#ff6b35] rounded-r px-2 py-1.5">
                        {t.pricingNote}
                      </div>
                    )}
                    {t.fullServiceNote && (
                      <div className="mt-2 text-xs text-[#333] italic">
                        {t.fullServiceNote}
                      </div>
                    )}
                    <Button
                      data-testid={`pricing-select-${t.id}`}
                      onClick={() => goBook(t.id)}
                      className="mt-5 bg-[#ff6b35] hover:bg-[#ff8c42] text-white font-bold rounded-lg"
                    >
                      Book this
                    </Button>
                  </div>
                </div>
              );

              if (TIER_HOVER[t.id]) {
                const h = TIER_HOVER[t.id];
                return (
                  <HoverCard key={t.id} openDelay={100} closeDelay={80}>
                    <HoverCardTrigger asChild>{card}</HoverCardTrigger>
                    <HoverCardContent
                      side="top"
                      align="center"
                      className="w-72 p-0 overflow-hidden border-[3px] border-[#ff6b35] rounded-xl shadow-xl"
                    >
                      <img
                        src={h.img}
                        alt={`${h.title} — staged in driveway`}
                        data-testid={`hover-image-${t.id}`}
                        className="w-full h-56 object-cover"
                      />
                      <div className="px-4 py-3 bg-white">
                        <div className="text-xs font-bold text-[#ff6b35] uppercase tracking-widest">{h.title}</div>
                        <div className="text-sm text-[#1a1a1a] mt-0.5">{h.sub}</div>
                      </div>
                    </HoverCardContent>
                  </HoverCard>
                );
              }
              return card;
            })}
          </div>
          <div className="mt-10 rounded-xl bg-[#1a1a1a] text-white p-5 sm:p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
            <div className="flex items-center gap-4 flex-1">
              <div className="flex-shrink-0 grid place-items-center w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[#ff6b35]">
                <Truck size={20} className="sm:hidden" />
                <Truck size={22} className="hidden sm:block" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-extrabold text-lg sm:text-xl md:text-2xl">Curbside &amp; driveway pickup only</h3>
                <p className="text-white/80 text-sm mt-1">
                  Just stage your stuff at the curb or in the driveway — we'll take it from there. No inside pickups.
                  Need a <span className="text-[#ff8c42] font-bold">full-service clean-out</span>? Give us a call for pricing.
                </p>
              </div>
            </div>
            <a
              href="tel:+12163062330"
              data-testid="curbside-call-link"
              className="inline-flex w-full md:w-auto justify-center items-center gap-2 bg-white text-[#ff6b35] hover:bg-[#fffbf0] font-bold rounded-lg px-4 py-3 sm:py-2.5 transition-colors flex-shrink-0"
            >
              <Phone size={16} /> (216) 306-2330
            </a>
          </div>
          <p className="text-center text-[#333] mt-8 text-sm">
            No surprises. No hidden fees. Price quoted is price you pay.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-12 sm:py-16 md:py-24 bg-[#fffbf0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <div className="text-[#ff6b35] font-bold text-sm uppercase tracking-widest mb-2">How it works</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold">Three steps. That's it.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: 1, t: "Book Online", d: "Pick your tier, pick a date, tell us where to go. Under 2 minutes." },
              { n: 2, t: "We Show Up", d: "Our uniformed crew arrives during your scheduled window. You don't even need to be home." },
              { n: 3, t: "We Haul Away", d: "We load everything up and drive off. Your space is clear — no trip to the dump, no weekend lost." },
            ].map((s) => (
              <div key={s.n} data-testid={`step-${s.n}`} className="relative bg-white rounded-xl p-8 border border-[#1a1a1a]/5 hover:-translate-y-1 transition-transform">
                <div className="grid place-items-center w-14 h-14 rounded-full bg-[#ff6b35] text-white font-display font-black text-2xl shadow-lg">
                  {s.n}
                </div>
                <h3 className="mt-5 font-display font-extrabold text-2xl">{s.t}</h3>
                <p className="mt-2 text-[#333]">{s.d}</p>
              </div>
            ))}
          </div>

          {/* STAGING GALLERY */}
          <div className="mt-16">
            <div className="text-center mb-8">
              <div className="text-[#ff6b35] font-bold text-sm uppercase tracking-widest mb-2">How to stage</div>
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold">Curbside or driveway. That's it.</h3>
              <p className="text-[#333] mt-2 max-w-xl mx-auto">
                Set your items where we can easily reach them. No need to be home. No inside jobs — unless you call for full-service pricing.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {STAGING_GALLERY.map((g, i) => (
                <figure
                  key={i}
                  data-testid={`staging-photo-${i}`}
                  className="group relative overflow-hidden rounded-xl border-2 border-[#ff6b35]/30 bg-white"
                >
                  <img
                    src={g.src}
                    alt={g.caption}
                    loading="lazy"
                    className="w-full h-56 object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#1a1a1a]/90 via-[#1a1a1a]/40 to-transparent text-white text-sm font-semibold p-3">
                    {g.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <div className="text-[#ff6b35] font-bold text-sm uppercase tracking-widest mb-2">Why choose us</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold">
              Built for Cleveland. Priced for everyone.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {BENEFITS.map((b, i) => {
              const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
              return (
                <div
                  key={b.title}
                  data-testid={`benefit-${i}`}
                  className="bg-[#fffbf0] border-l-[5px] border-[#ff6b35] rounded-r-xl p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center gap-3">
                    <Icon size={22} className="text-[#ff6b35]" />
                    <h3 className="font-display font-bold text-xl">{b.title}</h3>
                  </div>
                  <p className="mt-3 text-[#333]">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section id="area" className="py-12 sm:py-16 md:py-24 bg-[#1a1a1a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[#ff8c42] font-bold text-sm uppercase tracking-widest mb-2">Service area</div>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold">Cuyahoga County &amp; beyond.</h2>
            <p className="mt-4 text-white/80 text-lg max-w-md">
              We cover all of Cuyahoga County. Not sure if we reach you? Start a booking — we'll confirm on the spot.
            </p>
            <Button
              data-testid="area-book-btn"
              onClick={() => goBook()}
              className="mt-7 bg-white text-[#ff6b35] hover:bg-[#fffbf0] font-bold rounded-lg h-11 px-6"
            >
              Check availability
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {SERVICE_AREAS.map((a) => (
              <span
                key={a}
                data-testid={`area-tag-${a}`}
                className="px-4 py-2 rounded-full bg-white/10 hover:bg-[#ff6b35] hover:text-white border border-white/20 transition-colors text-sm font-medium cursor-default"
              >
                {a}
              </span>
            ))}
            <span className="px-4 py-2 rounded-full bg-[#ff6b35] text-white border border-[#ff6b35] text-sm font-bold">
              + More
            </span>
          </div>
        </div>
      </section>

      {/* WHAT WE REMOVE */}
      <section className="py-12 sm:py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-[#ff6b35] font-bold text-sm uppercase tracking-widest mb-2">What we haul</div>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold">The honest list.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div data-testid="we-remove-list" className="bg-[#fffbf0] rounded-xl p-8 border-l-[5px] border-[#ff6b35]">
              <h3 className="font-display font-extrabold text-2xl flex items-center gap-2">
                <Check className="text-[#ff6b35]" /> We Remove
              </h3>
              <ul className="mt-5 space-y-3">
                {WE_REMOVE.map((x) => (
                  <li key={x} className="flex gap-3 text-[#1a1a1a]">
                    <Check size={18} className="text-[#ff6b35] mt-0.5 flex-shrink-0" /> {x}
                  </li>
                ))}
              </ul>
            </div>
            <div data-testid="we-dont-remove-list" className="bg-white rounded-xl p-8 border-2 border-[#1a1a1a]/10">
              <h3 className="font-display font-extrabold text-2xl flex items-center gap-2 text-[#1a1a1a]">
                <X className="text-[#1a1a1a]" /> We Don't Remove
              </h3>
              <ul className="mt-5 space-y-3">
                {WE_DONT_REMOVE.map((x) => (
                  <li key={x} className="flex gap-3 text-[#333]">
                    <X size={18} className="text-[#1a1a1a] mt-0.5 flex-shrink-0" /> {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 md:py-24 bg-[#fffbf0]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-[#ff6b35] font-bold text-sm uppercase tracking-widest mb-2">FAQ</div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold">Questions, answered.</h2>
          </div>
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {FAQ_ITEMS.map((f, i) => (
              <AccordionItem key={i} value={`q-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-bold text-lg hover:text-[#ff6b35] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#333] text-base">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-10 text-center">
            <Link to="/faq" data-testid="view-more-faq" className="text-[#ff6b35] font-bold hover:underline">
              View all FAQs →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="hero-gradient text-white py-12 sm:py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-extrabold">
            Ready to reclaim your space?
          </h2>
          <p className="mt-3 text-white/90 text-lg">Book in under 2 minutes. We'll handle the rest.</p>
          <Button
            data-testid="cta-book-btn"
            onClick={() => goBook()}
            className="mt-7 bg-white text-[#ff6b35] hover:bg-[#fffbf0] font-bold rounded-lg h-12 px-8 text-base shadow-xl"
          >
            Book Your Pickup Now
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
